import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewChildren,
  QueryList,
  ElementRef,
  HostListener
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommunicationService } from '../../services/communication.service';
import { BaseService } from '../../shared/services/base.service';
import { AlertsService } from '../../services/alerts.service';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { LoaderService } from '../../services/loader.service';
import { finalize, firstValueFrom, map, Observable } from 'rxjs';

export interface HighlightItem {
  opid:            string;
  changes:         string;
  subject:         string;
  description:     string;
  image:           string[];
  module:          string[];
  helpdesk_id:     string;
  _imageDocIds?:   string[];
  images_loading?: boolean;
  images_error?:   boolean;
}

export interface IndexEntry {
  module:          string;
  slide:           string;
  images?:         string[];
  _imageDocIds?:   string[];
  images_loading?: boolean;
  images_error?:   boolean;
}

interface SummaryBadge {
  label:    string;
  count:    number;
  dotClass: string;
}

@Component({
  selector:    'app-release-note',
  templateUrl: './release-note.component.html',
  styleUrls:   ['./release-note.component.scss'],
  standalone:  false
})
export class ReleaseNoteComponent implements OnInit, AfterViewInit, OnDestroy {

  @ViewChild('sidebar',     { static: true }) sidebarRef!:     ElementRef<HTMLElement>;
  @ViewChild('backdrop',    { static: true }) backdropRef!:    ElementRef<HTMLElement>;
  @ViewChild('toggleBtn',   { static: true }) toggleBtnRef!:   ElementRef<HTMLButtonElement>;
  @ViewChild('drawerClose', { static: true }) drawerCloseRef!: ElementRef<HTMLButtonElement>;
  @ViewChildren('answerEl')                   answerEls!:       QueryList<ElementRef<HTMLElement>>;

  constructor(
    private communicationService: CommunicationService,
    private baseService:          BaseService,
    private alerts:               AlertsService,
    private sanitizer:            DomSanitizer,
    public  router:               Router,
    private route:                ActivatedRoute,
    private loaderService:        LoaderService
  ) {}

  readonly MOBILE_BP = 767.98;

  type:          string  = 'video/mp4';
  videoUrl:      string  = '';
  releaseNoteid: string  = '';
  videoReady:    boolean = false;

  config:       any    = { isRefer: false };
  versionName:  string = '';
  releaseTitle: string = '';
  summary:      string = '';
  releaseDate:  string = '';
  releaseNoteData: any;

  desktopOpen      = true;
  drawerOpen       = false;
  showCloseIcon    = true;
  showOpenIcon     = false;
  showToggleBtn    = true;
  sidebarOpenClass = true;
  archiveOpen      = false;
  releaseGroupOpen = true;
  activeId: any    = null;
  searchQuery      = '';

  mindMapSrc:     SafeUrl | null = null;
  mindMapError                   = false;
  mindMapLoading                 = false;

  pdfBase64Src: SafeUrl | null = null;
  pdfError                     = false;
  pdfLoading                   = false;
  pdfFilePath                  = '';   // PDF is fetched only when the user opens it
  private pdfBlobUrl           = '';

  pdfModalOpen: boolean = false;

  activeFaqIndex: number | null = null;
  faqItems: any[] = [];

  highlightItems:         HighlightItem[] = [];
  expandedHighlightIndex: number | null   = null;
  highlightSummary:       SummaryBadge[]  = [];

  selectedHighlight:  HighlightItem | null = null;
  highlightModalOpen: boolean              = false;

  carouselIndex: number = 0;

  lightboxOpen:   boolean  = false;
  lightboxImages: string[] = [];
  lightboxIndex:  number   = 0;

  // Index section
  indexItemsLeft:  IndexEntry[] = [];
  indexItemsRight: IndexEntry[] = [];

  // Index image modal
  indexModalOpen:   boolean          = false;
  indexModalItem:   IndexEntry | null = null;
  indexCarouselIdx: number           = 0;

  // ─────────────────────────────────────────────────────────────

  private get allItems(): any[] {
    if (!this.releaseNoteData) return [];
    return Array.isArray(this.releaseNoteData)
      ? this.releaseNoteData
      : [this.releaseNoteData];
  }

  get versionHistoryItems(): any[] {
    const list = this.allItems.filter((item: any) => item?.status !== 'archive');
    if (!this.searchQuery?.trim()) return list;
    const q = this.searchQuery.toLowerCase();
    return list.filter((item: any) =>
      item?.version_name?.toLowerCase().includes(q) ||
      item?.release_title?.toLowerCase().includes(q)
    );
  }

  get archiveItems(): any[] {
    const list = this.allItems.filter((item: any) => item?.status === 'archive');
    if (!this.searchQuery?.trim()) return list;
    const q = this.searchQuery.toLowerCase();
    return list.filter((item: any) =>
      item?.version_name?.toLowerCase().includes(q) ||
      item?.release_title?.toLowerCase().includes(q)
    );
  }

  ngOnInit(): void {
    if (this.isMobile()) {
      this.showCloseIcon    = false;
      this.showOpenIcon     = true;
      this.showToggleBtn    = true;
      this.sidebarOpenClass = false;
    }
    this.communicationService.emitSidebar({ sidebar: false });
    this.communicationService.emitChange({ hideMenuIcon: true });
    this.getReleaseNoteVideo();
  }

  ngAfterViewInit(): void {}

  ngOnDestroy(): void {
    document.body.style.overflow = '';
    if (this.pdfBlobUrl) { URL.revokeObjectURL(this.pdfBlobUrl); }
  }

  getReleaseNoteVideo(): void {
    this.baseService.getData('', '', 'release_notes', 'opsmgmt', 'GET_PAGE')
      .subscribe((res: any) => {
        if (res && res.errCode === 0) {
          // Draft release notes are not published yet — keep them off the screen
          const resources = (Array.isArray(res.resource) ? res.resource : [res.resource])
            .filter((r: any) => r && String(r.status ?? '').toLowerCase() !== 'draft');
          this.releaseNoteData = resources;

          const urlId     = this.route.snapshot.paramMap.get('id');
          const matched   = urlId ? resources.find((r: any) => r.id === urlId) : null;
          const firstItem = matched ?? resources[0];

          if (firstItem) {
            this.loadItem(firstItem);
            if (!urlId) {
              this.router.navigate(['/release-notes', firstItem.id], { replaceUrl: true });
            }
          }
        } else {
          this.alerts.error(res.message);
        }
      });
  }

  private loadItem(item: any): void {
  this.activeId      = item.id;
  this.releaseNoteid = item.id;
  this.versionName   = item.version_name  || '';
  this.releaseTitle  = item.release_title || '';
  this.summary       = item.summary       || '';
  this.releaseDate   = item.release_date  || '';

  // ── Video ──────────────────────────────────────────────────
  this.videoReady = false;
  this.videoUrl   = item.video_url || '';
  setTimeout(() => { this.videoReady = true; }, 0);

  // ── Mind Map ───────────────────────────────────────────────
  this.mindMapSrc   = null;   // hides section immediately
  this.mindMapError = false;
  if (item.release_note_image?.length > 0) {
    const documentId = item.release_note_image[0]?.documentId;
    if (documentId) { this.fetchMindMapImage(documentId); }
  }

  // ── PDF (fetched on Open click) ────────────────────────────
  if (this.pdfBlobUrl) { URL.revokeObjectURL(this.pdfBlobUrl); this.pdfBlobUrl = ''; }
  this.pdfBase64Src = null;
  this.pdfError     = false;
  this.pdfLoading   = false;
  this.pdfModalOpen = false;
  this.pdfFilePath  = item.file_path || '';

  // ── FAQ ────────────────────────────────────────────────────
  this.faqItems       = [];   // hides section immediately
  this.activeFaqIndex = null;
  this.getFaqItems(item.version_name);

  // ── Highlights ─────────────────────────────────────────────
  this.highlightItems         = [];   // hides section immediately
  this.highlightSummary       = [];
  this.expandedHighlightIndex = null;
  this.selectedHighlight      = null;
  this.highlightModalOpen     = false;
  this.carouselIndex          = 0;
  this.getHighlightItems(item.id);

  // ── Lightbox ───────────────────────────────────────────────
  this.lightboxOpen   = false;
  this.lightboxImages = [];
  this.lightboxIndex  = 0;

  // ── Index / Module Overview ────────────────────────────────
  this.indexItemsLeft   = [];   // hides section immediately
  this.indexItemsRight  = [];
  this.indexModalOpen   = false;
  this.indexModalItem   = null;
  this.indexCarouselIdx = 0;
  this.loadIndexItems(item);

  // ── Body scroll unlock (safety) ────────────────────────────
  document.body.style.overflow = '';
}

  onClick(id: any): void {
    if (!id) return;
    const resources = Array.isArray(this.releaseNoteData)
      ? this.releaseNoteData
      : [this.releaseNoteData];
    const item = resources.find((obj: any) => obj.id === id);
    if (!item) return;
    this.router.navigate(['/release-notes', id]);
    this.loadItem(item);
  }

  fetchMindMapImage(documentId: string): void {
    this.mindMapLoading = true;
    this.mindMapError = false;
    this.mindMapSrc = null;

    this.baseService
      .getfileDataFromURL(documentId, 'helpdesk_documents')
      .then((res: any[]) => {
        if (res && res.length > 0 && res[0]?.imageSrc) {

          this.getImageData(documentId, {
            folder_name: res[0].file.name
          }).subscribe({
            next: (safeUrl: SafeUrl) => {
              this.mindMapSrc = safeUrl;
              this.mindMapLoading = false;
            },
            error: () => {
              this.mindMapError = true;
              this.mindMapLoading = false;
            }
          });

        } else {
          this.mindMapError = true;
          this.mindMapLoading = false;
        }
      })
      .catch(() => {
        this.mindMapError = true;
        this.mindMapLoading = false;
      });
  }

  fetchPdfDocument(documentId: string): void {
    if (!documentId) return;
    const url = this.baseService.getApiEndpoint('dms') +
      'helpdesk_documents/image-response-entity?path=' + documentId + '&application=dms';
    this.pdfLoading = true;
    this.pdfError   = false;
    this.baseService.getFileByUrl(url, false).subscribe({
      next: (res: any) => {
        // user switched release while the PDF was downloading
        if (this.pdfFilePath !== documentId) return;
        if (this.pdfBlobUrl) { URL.revokeObjectURL(this.pdfBlobUrl); }
        this.pdfBlobUrl   = URL.createObjectURL(res);
        this.pdfBase64Src = this.pdfBlobUrl;
        this.pdfLoading   = false;
      },
      error: () => {
        if (this.pdfFilePath !== documentId) return;
        this.pdfLoading = false;
        this.pdfError   = true;
      }
    });
  }

  getFaqItems(versionName: string): void {
    this.baseService.getData('', '', 'knowledge_faq', 'opsmgmt', 'GET_PAGE')
      .subscribe((res: any) => {
        if (res && res.errCode === 0) {
          const resources = Array.isArray(res.resource) ? res.resource : [res.resource];

          this.faqItems = resources
            .filter((item: any) => item?.version_name === versionName)
            .map((item: any) => ({
              question:      item.question  || '',
              answer:        item.answer    || '',
              video_url:     item.video_url || '',
              _imageDocId:   item.knowledge_faq_image?.[0]?.documentId || '',
              image_src:     null as SafeUrl | null,
              image_loading: false,
              image_error:   false
            }));

          this.activeFaqIndex = null;
          this.answerEls?.forEach(el => {
            el.nativeElement.style.height = '0px';
          });
          // FAQ images are fetched when the answer is expanded (toggleFaq)

        } else {
          this.faqItems = [];
        }
      });
  }

  getHighlightItems(releaseNoteId: string): void {
    const data = {
      filter : "release_notes_id:"+releaseNoteId+",is_highlighted:true"
    }
    this.baseService.getData(data, '', 'release_task', 'opsmgmt', 'GET_PAGE')
      .subscribe((res: any) => {
        if (res && res.errCode === 0) {
          const resources = Array.isArray(res.resource) ? res.resource : [res.resource];

          this.highlightItems = resources.map((item: any) => {
            const docIds: string[] = (item.release_task_image || [])
              .map((img: any) => img?.documentId)
              .filter(Boolean);

            return {
              opid:           item.opid        || '',
              changes:        item.changes     || '',
              subject:        item.subject     || '',
              description:    item.description || '',
              image:          [],
              // ✅ FIX: module_name is a string, wrap it in an array for the template
              module:         item.module_name
                                ? [item.module_name]
                                : (Array.isArray(item.module) ? item.module : []),
              helpdesk_id:    item.helpdesk_id || '',
              _imageDocIds:   docIds,
              images_loading: false   // images are fetched when the detail modal opens
            } as HighlightItem;
          });

          this.buildHighlightSummary();

        } else {
          this.highlightItems   = [];
          this.highlightSummary = [];
        }
      });
  }

private loadIndexItems(item: any): void {
  const versionName = item.version_name;
  if (!versionName) return;

  const moduleList: { id: string; text: string }[] = Array.isArray(item.module)
    ? item.module.map((m: any) => ({ id: m.id, text: m.text || '' })).filter((m: any) => m.id)
    : [{ id: item.module?.id, text: item.module?.text || '' }].filter((m: any) => m.id);

  if (!moduleList.length) return;

  const requests = moduleList.map((mod) => {
    const data = { 
      "args": `id:${mod.id},releaseNoteId:${this.releaseNoteid}`
    };
    return this.baseService.getData(data, '', 'application_module', 'opsmgmt', 'GET_IMAGES_BY_MODULE_ID')
      .toPromise()
      .then((res: any) => ({ res, moduleName: mod.text }));
  });

  Promise.all(requests).then((results: any[]) => {
    let allEntries: IndexEntry[] = [];

    results.forEach(({ res, moduleName }) => {
      if (res && res.errCode === 0) {
        const resources = Array.isArray(res.resource) ? res.resource : [res.resource];
        resources.forEach((entry: any) => {
          const docIds: string[] = (entry.module_img || [])
            .map((img: any) => img?.documentId)
            .filter(Boolean);

          allEntries.push({
            module:         moduleName,
            slide:          '',
            images:         [],
            _imageDocIds:   docIds,
            images_loading: false   // slide images are fetched when "View" is clicked
          } as IndexEntry);
        });
      }
    });

    const half = Math.ceil(allEntries.length / 2);
    this.indexItemsLeft  = allEntries.slice(0, half);
    this.indexItemsRight = allEntries.slice(half);

  }).catch(() => {
    this.indexItemsLeft  = [];
    this.indexItemsRight = [];
  });
}

getImageData(documentId: string, list?: any): Observable<SafeUrl> {

    let imageUrl = '';

    if (documentId && documentId.includes('/')) {
      imageUrl =
        '/dms/api/v1/' +
        (list?.document_type || 'helpdesk_documents') +
        '/image-response-entity?path=' +
        documentId;
    } else {
      imageUrl =
        this.baseService.getApiEndpoint('dms') +
        (list?.document_type || 'helpdesk_documents') +
        '/image-response-entity?args=id:' +
        documentId +
        '&application=dms';
    }

    this.loaderService.showSpinner();

    return this.baseService.getFileByUrl(imageUrl, false).pipe(
      map((blob: Blob) => this.sanitizer.bypassSecurityTrustUrl(
        window.URL.createObjectURL(blob)
      )),
      // hide on error too, otherwise a failed on-click fetch leaves the spinner stuck
      finalize(() => this.loaderService.hideSpinner())
    );
  }

  // Resolve DMS document ids to displayable image urls; failed ids are dropped
  private loadImages(docIds: string[]): Promise<SafeUrl[]> {
    const imagePromises = docIds.map((docId: string) =>
      this.baseService.getfileDataFromURL(docId, 'helpdesk_documents')
        .then((imgRes: any) => {
          if (imgRes && imgRes.length > 0) {
            return firstValueFrom(
              this.getImageData(docId, {
                folder_name: imgRes[0]?.file?.name,
                document_type: 'helpdesk_documents'
              })
            );
          }
          return null;
        })
        .catch(() => null)
    );
    return Promise.all(imagePromises)
      .then((resolvedImages: (SafeUrl | null)[]) => resolvedImages.filter(Boolean) as SafeUrl[]);
  }

  // Fetches a highlight's / module entry's images once, on first open
  private ensureImagesLoaded(entry: HighlightItem | IndexEntry, target: 'image' | 'images'): void {
    const docIds = entry._imageDocIds || [];
    const loaded = (entry as any)[target] as any[] | undefined;
    if (!docIds.length || loaded?.length || entry.images_loading) return;
    entry.images_loading = true;
    entry.images_error   = false;
    this.loadImages(docIds).then((resolved) => {
      (entry as any)[target] = resolved;
      entry.images_loading   = false;
      entry.images_error     = resolved.length === 0;
    });
  }

  // ─────────────────────────────────────────────────────────────

  // ── Index image modal methods ─────────────────────────────────
  openIndexModal(entry: IndexEntry, event: Event): void {
    event.stopPropagation();
    this.indexModalItem   = entry;
    this.indexCarouselIdx = 0;
    this.indexModalOpen   = true;
    document.body.style.overflow = 'hidden';
    this.ensureImagesLoaded(entry, 'images');
}

  closeIndexModal(): void {
    this.indexModalOpen   = false;
    this.indexModalItem   = null;
    this.indexCarouselIdx = 0;
    document.body.style.overflow = '';
  }

  indexCarouselNext(): void {
    if (!this.indexModalItem?.images?.length) return;
    this.indexCarouselIdx =
      (this.indexCarouselIdx + 1) % this.indexModalItem.images.length;
  }

  indexCarouselPrev(): void {
    if (!this.indexModalItem?.images?.length) return;
    this.indexCarouselIdx =
      (this.indexCarouselIdx - 1 + this.indexModalItem.images.length) %
      this.indexModalItem.images.length;
  }

  indexCarouselGoTo(i: number): void {
    this.indexCarouselIdx = i;
  }
  // ─────────────────────────────────────────────────────────────

  toggleFaq(index: number): void {
    const els = this.answerEls.toArray();

    if (this.activeFaqIndex !== null && this.activeFaqIndex !== index) {
      const prev = els[this.activeFaqIndex]?.nativeElement;
      if (prev) prev.style.height = '0px';
    }

    if (this.activeFaqIndex === index) {
      const el = els[index]?.nativeElement;
      if (el) el.style.height = '0px';
      this.activeFaqIndex = null;
    } else {
      const el = els[index]?.nativeElement;
      if (el) el.style.height = el.scrollHeight + 'px';
      this.activeFaqIndex = index;
      this.loadFaqImage(index);
    }
  }

  private loadFaqImage(index: number): void {
    const faq = this.faqItems[index];
    if (!faq?._imageDocId || faq.image_src || faq.image_loading) return;
    faq.image_loading = true;
    faq.image_error   = false;
    this.loadImages([faq._imageDocId]).then((resolved) => {
      faq.image_src     = resolved[0] || null;
      faq.image_error   = resolved.length === 0;
      faq.image_loading = false;
      this.refreshFaqHeight(index);
    });
  }

  // Re-measure an open answer once its lazily loaded content changes its height
  refreshFaqHeight(index: number): void {
    if (this.activeFaqIndex !== index) return;
    setTimeout(() => {
      const el = this.answerEls?.toArray()[index]?.nativeElement;
      if (el && this.activeFaqIndex === index) el.style.height = el.scrollHeight + 'px';
    }, 0);
  }

  private buildHighlightSummary(): void {
    const map: Record<string, { label: string; dotClass: string; count: number }> = {};

    this.highlightItems.forEach(item => {
      const key   = this.getChangeBadgeKey(item.changes);
      const label = item.changes;
      if (!map[key]) {
        map[key] = { label, dotClass: 'dot-' + key, count: 0 };
      }
      map[key].count++;
    });

    this.highlightSummary = Object.values(map);
  }

  toggleHighlight(index: number): void {
    this.expandedHighlightIndex = this.expandedHighlightIndex === index ? null : index;
  }

  openPdfModal(): void {
    this.pdfModalOpen = true;
    document.body.style.overflow = 'hidden';
    if (!this.pdfBase64Src && !this.pdfLoading) { this.fetchPdfDocument(this.pdfFilePath); }
  }

  closePdfModal(): void {
    this.pdfModalOpen = false;
    document.body.style.overflow = '';
  }

  openHighlightModal(item: HighlightItem, event: Event): void {
    event.stopPropagation();
    this.selectedHighlight  = item;
    this.highlightModalOpen = true;
    this.carouselIndex      = 0;
    document.body.style.overflow = 'hidden';
    this.ensureImagesLoaded(item, 'image');
  }

  closeHighlightModal(): void {
    this.highlightModalOpen = false;
    this.selectedHighlight  = null;
    this.carouselIndex      = 0;
    document.body.style.overflow = '';
  }

  carouselNext(): void {
    if (!this.selectedHighlight) return;
    this.carouselIndex = (this.carouselIndex + 1) % this.selectedHighlight.image.length;
  }

  carouselPrev(): void {
    if (!this.selectedHighlight) return;
    this.carouselIndex =
      (this.carouselIndex - 1 + this.selectedHighlight.image.length) %
      this.selectedHighlight.image.length;
  }

  carouselGoTo(index: number): void {
    this.carouselIndex = index;
  }

  getChangeBadgeClass(change: string): string {
    return 'badge-' + this.getChangeBadgeKey(change);
  }

  private getChangeBadgeKey(change: string): string {
    if (!change) return 'default';
    const val = change.toLowerCase();
    if (val.includes('new feature'))              return 'new-feature';
    if (val.includes('functionality'))            return 'functionality';
    if (val.includes('workflow'))                 return 'workflow';
    if (val.includes('ui') || val.includes('ux')) return 'ui-ux';
    if (val.includes('bug'))                      return 'bug-fix';
    return 'default';
  }

  openLightbox(images: string[], index: number): void {
    this.lightboxImages = images;
    this.lightboxIndex  = index;
    this.lightboxOpen   = true;
    document.body.style.overflow = 'hidden';
  }

  closeLightbox(): void {
    this.lightboxOpen = false;
    document.body.style.overflow = '';
  }

  lightboxNext(): void {
    this.lightboxIndex = (this.lightboxIndex + 1) % this.lightboxImages.length;
  }

  lightboxPrev(): void {
    this.lightboxIndex = (this.lightboxIndex - 1 + this.lightboxImages.length) % this.lightboxImages.length;
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      if (this.lightboxOpen)       { this.closeLightbox();       return; }
      if (this.indexModalOpen)     { this.closeIndexModal();     return; }
      if (this.highlightModalOpen) { this.closeHighlightModal(); return; }
      if (this.pdfModalOpen)       { this.closePdfModal();       return; }
    }
    if (this.lightboxOpen) {
      if (event.key === 'ArrowRight') { this.lightboxNext(); }
      if (event.key === 'ArrowLeft')  { this.lightboxPrev(); }
      return;
    }
    if (this.indexModalOpen) {
      if (event.key === 'ArrowRight') { this.indexCarouselNext(); }
      if (event.key === 'ArrowLeft')  { this.indexCarouselPrev(); }
      return;
    }
    if (this.highlightModalOpen) {
      if (event.key === 'ArrowRight') { this.carouselNext(); }
      if (event.key === 'ArrowLeft')  { this.carouselPrev(); }
    }
  }

  onToggle(): void {
    if (this.isMobile()) {
      this.drawerOpen ? this.closeDrawer() : this.openDrawer();
    } else {
      this.desktopOpen ? this.closeDesktop() : this.openDesktop();
    }
  }

  onDrawerClose(): void   { this.closeDrawer(); }
  onBackdropClick(): void { this.closeDrawer(); }

  toggleArchive(): void      { this.archiveOpen      = !this.archiveOpen; }
  toggleReleaseGroup(): void { this.releaseGroupOpen = !this.releaseGroupOpen; }
  onMindMapError(): void     { this.mindMapError     = true; }

  @HostListener('window:resize')
  onResize(): void {
    if (!this.isMobile()) {
      this.sidebarRef.nativeElement.classList.remove('drawer-open');
      this.backdropRef.nativeElement.classList.remove('show');
      document.body.style.overflow = '';
      this.drawerOpen    = false;
      this.showToggleBtn = true;
      this.desktopOpen ? this.openDesktop() : this.closeDesktop();
    } else {
      this.sidebarRef.nativeElement.classList.remove('collapsed');
      this.sidebarOpenClass = false;
      this.showCloseIcon    = this.drawerOpen;
      this.showOpenIcon     = !this.drawerOpen;
      this.showToggleBtn    = !this.drawerOpen;
    }
  }

  private isMobile(): boolean { return window.innerWidth <= this.MOBILE_BP; }

  private openDrawer(): void {
    this.drawerOpen  = true;
    this.sidebarRef.nativeElement.classList.add('drawer-open');
    this.backdropRef.nativeElement.classList.add('show');
    document.body.style.overflow = 'hidden';
    this.showCloseIcon = true;
    this.showOpenIcon  = false;
    this.showToggleBtn = false;
  }

  private closeDrawer(): void {
    this.drawerOpen  = false;
    this.sidebarRef.nativeElement.classList.remove('drawer-open');
    this.backdropRef.nativeElement.classList.remove('show');
    document.body.style.overflow = '';
    this.showCloseIcon = false;
    this.showOpenIcon  = true;
    this.showToggleBtn = true;
  }

  private openDesktop(): void {
    this.desktopOpen = true;
    this.sidebarRef.nativeElement.classList.remove('collapsed');
    this.sidebarOpenClass = true;
    this.showCloseIcon    = true;
    this.showOpenIcon     = false;
  }

  private closeDesktop(): void {
    this.desktopOpen = false;
    this.sidebarRef.nativeElement.classList.add('collapsed');
    this.sidebarOpenClass = false;
    this.showCloseIcon    = false;
    this.showOpenIcon     = true;
  }
}